import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ShieldCheck,
  Bell,
  ChevronRight,
  ChevronLeft,
  Sun,
  TrendingDown,
  ArrowRight,
  Search,
  DollarSign,
  KeyRound,
  Globe,
  Grid3X3,
  Wifi as WifiIcon,
  WifiOff,
  Tag,
  Users,
  Lock,
  Loader2,
  Check,
} from "lucide-react";
import { Sprout } from "lucide-react";
import { LanguageSwitcher } from "../../LanguageSwitcher";
import { AlertsOptIn } from "../AlertsOptIn";
import { ReferralCard } from "../ReferralCard";
import { ForestSubView } from "../MyForest";
import { useVehicle } from "../../../lib/vehicleContext";
import { warrantyFor } from "../../../lib/brand";
import { getClimate } from "../../../lib/climateData";
import {
  useSubscription,
  openSubscribeGate,
  isOwnerMode,
  getEffectiveTier,
  hasPaidSubscription,
  markSubscribed,
} from "../../../lib/subscription";
import {
  WeatherChargingView,
  CostSavingsView,
  MarketplaceView,
  CohortView,
  FleetView,
  isPremium,
} from "../PremiumViews";
import {
  API_BASE,
  HealthChart,
  MoreTile,
  PremiumTile,
  SubHeader,
  recordWarrantyClaim,
  buildCells,
  BUCKETS,
} from "./_shared";

export const MoreView = ({ sub, setSub, stateCode }) => {
  const { t } = useTranslation();
  const { vehicle } = useVehicle();
  const climate = useMemo(() => getClimate(stateCode || "CA"), [stateCode]);
  const premium = isPremium();
  const upgrade = () => setSub("pricing");

  if (sub === "warranty") return <WarrantySubView back={() => setSub(null)} />;
  if (sub === "cells") return <CellsSubView back={() => setSub(null)} />;
  if (sub === "lookup") return <LookupSubView back={() => setSub(null)} />;
  if (sub === "alerts") return <AlertsSubView back={() => setSub(null)} />;
  if (sub === "pricing") return <PricingSubView back={() => setSub(null)} />;
  if (sub === "weather") return <WeatherChargingView back={() => setSub(null)} vehicle={vehicle} climate={climate} onUpgrade={upgrade} />;
  if (sub === "cost") return <CostSavingsView back={() => setSub(null)} vehicle={vehicle} onUpgrade={upgrade} />;
  if (sub === "market") return <MarketplaceView back={() => setSub(null)} vehicle={vehicle} onUpgrade={upgrade} />;
  if (sub === "cohort") return <CohortView back={() => setSub(null)} vehicle={vehicle} onUpgrade={upgrade} />;
  if (sub === "fleet") return <FleetView back={() => setSub(null)} vehicle={vehicle} onUpgrade={upgrade} />;
  if (sub === "forest") return <ForestSubView back={() => setSub(null)} />;

  return (
    <div className="pt-4 space-y-2" data-testid="mobile-app-view-more">
      <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1 px-1">
        {t("app.more_tools", "Tools")}
      </div>
      <MoreTile
        icon={Grid3X3}
        color="#39FF14"
        title={t("app.more_cells", "Cell health map")}
        desc={t("app.more_cells_desc", "Per-cell health, VIN-linked")}
        onClick={() => setSub("cells")}
        testid="more-cells"
      />
      <MoreTile
        icon={ShieldCheck}
        color="#39FF14"
        title={t("app.more_warranty", "Warranty countdown")}
        desc={t("app.more_warranty_desc", "Time & miles until Tesla's coverage ends")}
        onClick={() => setSub("warranty")}
        testid="more-warranty"
      />
      <MoreTile
        icon={Sprout}
        color="#39FF14"
        title={t("app.more_forest", "My Forest")}
        desc={t("app.more_forest_desc", "Trees planted in your name")}
        onClick={() => setSub("forest")}
        testid="more-forest"
      />
      <MoreTile
        icon={Search}
        color="#00E5FF"
        title={t("app.more_lookup", "Score lookup by VIN")}
        desc={t("app.more_lookup_desc", "Get an EVIQ Score™ for any Tesla")}
        onClick={() => setSub("lookup")}
        testid="more-lookup"
      />
      <MoreTile
        icon={Bell}
        color="#FF6B57"
        title={t("app.more_alerts", "SMS Alerts")}
        desc={t("app.more_alerts_desc", "Coming soon · Heat warnings, warranty risk, weekly reports")}
        onClick={() => setSub("alerts")}
        testid="more-alerts"
      />
      <MoreTile
        icon={DollarSign}
        color="#FFB020"
        title={t("app.more_pricing", "Plans & pricing")}
        desc={t("app.more_pricing_desc", "Basic · Premium · Lifetime — manage subscription")}
        onClick={() => setSub("pricing")}
        testid="more-pricing"
      />

      <div className="pt-4 pb-1 px-1 flex items-baseline justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
          Premium features
        </span>
        {!premium && (
          <span
            className="font-mono text-[9px] uppercase px-2 py-0.5 rounded-full border"
            style={{
              color: "#FFB020",
              borderColor: "rgba(255,176,32,0.4)",
              background: "rgba(255,176,32,0.08)",
            }}
            data-testid="premium-badge-lock"
          >
            Locked
          </span>
        )}
      </div>

      <PremiumTile icon={Sun} color="#FFB020" title="Weather-aware charging"
        desc="Charge when the pack is happiest, save when it's coldest"
        locked={!premium} onClick={() => setSub("weather")} testid="more-weather" />
      <PremiumTile icon={DollarSign} color="#39FF14" title="Cost savings"
        desc="Lifetime vs-gas savings + off-peak breakdown"
        locked={!premium} onClick={() => setSub("cost")} testid="more-cost" />
      <PremiumTile icon={Tag} color="#00E5FF" title="Marketplace value"
        desc="Live comparables + EVIQ resale premium"
        locked={!premium} onClick={() => setSub("market")} testid="more-market" />
      <PremiumTile icon={Users} color="#00E5FF" title="Cohort benchmark"
        desc="How you rank vs same year, model, mileage"
        locked={!premium} onClick={() => setSub("cohort")} testid="more-cohort" />
      <PremiumTile icon={Globe} color="#39FF14" title="Fleet evaluation"
        desc="Global rank + 12-month trend vs fleet median"
        locked={!premium} onClick={() => setSub("fleet")} testid="more-fleet" />

      <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mt-6 mb-1 px-1">
        {t("app.more_about", "About")}
      </div>
      <OwnerUnlockTile />
      <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-3 flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center border border-white/10 shrink-0"
          style={{ background: "rgba(0,229,255,0.08)", borderColor: "rgba(0,229,255,0.3)" }}
        >
          <Globe className="w-4 h-4" style={{ color: "#00E5FF" }} strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-white font-semibold">Language</div>
          <div className="text-[11px] text-zinc-500 truncate">
            English · Español · Français · Deutsch
          </div>
        </div>
        <LanguageSwitcher compact />
      </div>
      <Link
        to="/"
        className="block rounded-xl border border-white/10 bg-white/[0.02] px-3 py-3"
        data-testid="more-back-to-web"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center border border-white/10 shrink-0">
            <ChevronLeft className="w-4 h-4 text-zinc-400" strokeWidth={2.5} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-white font-semibold">
              {t("app.back_to_web", "Back to website")}
            </div>
            <div className="text-[11px] text-zinc-500">
              {t("app.back_to_web_desc", "Return to EVIQ marketing site")}
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};

// ---- Owner unlock tile (persistent per-device paywall bypass) ----
const OwnerUnlockTile = () => {
  const [ownerOn, setOwnerOn] = useState(() => isOwnerMode());

  useEffect(() => {
    const refresh = () => setOwnerOn(isOwnerMode());
    window.addEventListener("eviq:subscription-changed", refresh);
    return () => window.removeEventListener("eviq:subscription-changed", refresh);
  }, []);

  const activate = () => {
    const secret = window.prompt(
      "Owner unlock — enter the shared secret to bypass the paywall on this device:",
      "",
    );
    if (secret == null) return;
    if (secret === "EVIQ_OWNER_STURGIS_2026") {
      try {
        localStorage.setItem("eviq.owner_mode", "1");
        localStorage.setItem("eviq.tesla_user_name", "Sturgis Griffin");
      } catch (_e) {}
      window.dispatchEvent(new CustomEvent("eviq:subscription-changed"));
      setOwnerOn(true);
    } else {
      window.alert("Incorrect secret. Owner mode was not enabled.");
    }
  };

  const deactivate = () => {
    try {
      localStorage.removeItem("eviq.owner_mode");
    } catch (_e) {}
    window.dispatchEvent(new CustomEvent("eviq:subscription-changed"));
    setOwnerOn(false);
  };

  return (
    <button
      type="button"
      onClick={ownerOn ? deactivate : activate}
      data-testid={ownerOn ? "more-owner-exit" : "more-owner-unlock"}
      className="w-full text-left rounded-xl border px-3 py-3 flex items-center gap-3 transition-colors"
      style={{
        borderColor: ownerOn ? "rgba(255,176,32,0.40)" : "rgba(255,255,255,0.10)",
        background: ownerOn ? "rgba(255,176,32,0.06)" : "rgba(255,255,255,0.02)",
      }}
    >
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
        style={{
          background: "rgba(255,176,32,0.14)",
          border: "1px solid rgba(255,176,32,0.40)",
        }}
      >
        <KeyRound className="w-4 h-4" style={{ color: "#FFB020" }} strokeWidth={2.4} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-white font-semibold">
          {ownerOn ? "Exit owner mode" : "Owner unlock"}
        </div>
        <div className="text-[11px] text-zinc-500 truncate">
          {ownerOn
            ? "Currently bypassing the paywall on this device"
            : "Bypass the paywall on this device (owner-only)"}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" strokeWidth={2} />
    </button>
  );
};

// ---- Warranty sub-view ----
const WarrantySubView = ({ back }) => {
  const { vehicle } = useVehicle();
  const monthsPerPct = 1 / Math.abs(vehicle.delta30d || 0.2);
  const pctUntilFloor = vehicle.sohCurrent - (vehicle.warranty?.threshold || 70);
  const monthsUntilFloor = Math.round(pctUntilFloor * monthsPerPct);
  const yrsFloor = Math.floor(monthsUntilFloor / 12);
  const moFloor = monthsUntilFloor % 12;

  const now = new Date();
  const buildYear = vehicle.year || 2020;
  const warrantyEnd = vehicle.inServiceDate
    ? (() => {
        const d0 = new Date(vehicle.inServiceDate);
        return new Date(d0.getFullYear() + (vehicle.warranty?.years || 8), d0.getMonth(), d0.getDate());
      })()
    : new Date(
        buildYear + (vehicle.warranty?.years || 8),
        now.getMonth(),
        now.getDate()
      );

  const monthsLeftTotal = Math.max(
    0,
    (warrantyEnd.getFullYear() - now.getFullYear()) * 12 +
      (warrantyEnd.getMonth() - now.getMonth())
  );
  const yrs = Math.floor(monthsLeftTotal / 12);
  const mos = monthsLeftTotal % 12;
  const isExpired = warrantyEnd <= now;

  const w = warrantyFor(vehicle);
  const milesRemaining = Math.max(0, w.miles - vehicle.odometer);
  const milesPct = Math.min(100, (vehicle.odometer / w.miles) * 100);
  const sohBuffer = vehicle.sohCurrent - w.threshold;
  const willCross = !isExpired && monthsUntilFloor < monthsLeftTotal;

  return (
    <div className="space-y-4 pb-4" data-testid="mobile-app-view-warranty">
      <SubHeader title="Warranty countdown" back={back} />

      <div
        className="rounded-xl border p-4"
        style={{
          borderColor: isExpired ? "rgba(255,107,87,0.35)" : "rgba(57,255,20,0.25)",
          background: isExpired ? "rgba(255,107,87,0.05)" : "rgba(57,255,20,0.03)",
        }}
      >
        <div
          className="font-mono text-[10px] uppercase tracking-wider mb-2"
          style={{ color: isExpired ? "#FF6B57" : "#39FF14" }}
        >
          {isExpired ? "Warranty expired" : "Time remaining"}
        </div>
        {isExpired ? (
          <div className="font-display text-3xl text-white font-semibold leading-tight">
            Out of coverage
          </div>
        ) : (
          <div className="font-display text-4xl text-white font-semibold leading-none">
            {yrs}
            <span className="text-lg text-zinc-500 font-mono ml-1.5">yr</span>{" "}
            {mos}
            <span className="text-lg text-zinc-500 font-mono ml-1.5">mo</span>
          </div>
        )}
        <div className="mt-2 text-xs text-zinc-400">
          {isExpired ? "Ended " : "Expires "}
          {warrantyEnd.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="font-mono text-[10px] uppercase text-zinc-500">Miles headroom</span>
            <span className="text-xs font-mono text-white">{milesRemaining.toLocaleString()} mi</span>
          </div>
          <div className="h-2 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${milesPct}%`, background: "linear-gradient(90deg,#39FF14,#00E5FF)" }} />
          </div>
          <div className="flex items-center justify-between mt-1 font-mono text-[9px] text-zinc-500">
            <span>{vehicle.odometer.toLocaleString()} mi</span>
            <span>{warrantyFor(vehicle).miles.toLocaleString()} mi cap</span>
          </div>
        </div>

        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="font-mono text-[10px] uppercase text-zinc-500">SoH vs 70% floor</span>
            <span className="text-xs font-mono text-[#39FF14]">+{sohBuffer.toFixed(1)}%</span>
          </div>
          <div className="h-2 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${vehicle.sohCurrent}%`, background: "#39FF14" }} />
          </div>
          <div className="flex items-center justify-between mt-1 font-mono text-[9px] text-zinc-500">
            <span>{vehicle.sohCurrent}% current</span>
            <span>{vehicle.warranty?.threshold || 70}% warranty</span>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-[#FFB020]/25 bg-[#FFB020]/[0.04] p-3 flex items-start gap-2.5">
        <TrendingDown className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#FFB020" }} strokeWidth={2} />
        <div>
          <div className="text-sm text-white font-semibold leading-snug">
            {willCross
              ? `Eligibility projected at ${yrsFloor}yr ${moFloor}mo`
              : `Stays above ${vehicle.warranty?.threshold || 70}% through warranty`}
          </div>
          <div className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
            {willCross ? (
              <>You&apos;ll cross the {vehicle.warranty?.threshold || 70}% floor <b className="text-[#FFB020]">before</b> your warranty expires. A replacement claim is likely.</>
            ) : (
              <>Great news — at current pace, no warranty claim expected.</>
            )}
          </div>
        </div>
      </div>

      <button
        onClick={() => {
          recordWarrantyClaim(vehicle.sohCurrent);
          toast.success("Service request drafted", {
            description: `Report queued for your ${vehicle.year} ${vehicle.short}.`,
          });
        }}
        className="w-full brand-gradient text-black font-semibold rounded-xl py-3 text-sm inline-flex items-center justify-center gap-2"
        data-testid="mobile-app-warranty-cta"
      >
        Prepare service request
        <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
      </button>
    </div>
  );
};

// ---- Score Lookup sub-view ----
const LookupSubView = ({ back }) => {
  const { t } = useTranslation();
  const { vehicle } = useVehicle();
  const { isSubscribed } = useSubscription();
  const [vin, setVin] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!isSubscribed) {
      openSubscribeGate("vin-lookup");
      return;
    }
    if (vin.length < 11) {
      toast.error("VIN too short", { description: "Enter at least an 11-character VIN prefix." });
      return;
    }
    if (!email.includes("@")) {
      toast.error("Email required", { description: "A valid email is needed to receive your report." });
      return;
    }
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const payload = {
        email,
        vin: vin.toUpperCase(),
        model_label: `${vehicle.year} ${vehicle.name || vehicle.short}`,
        model_year: vehicle.year,
        epa_range: vehicle.epaRange,
        current_range: vehicle.currentRange || Math.round(vehicle.epaRange * (vehicle.sohCurrent / 100)),
        odometer: vehicle.odometer,
        soh: vehicle.sohCurrent,
        warranty_years: warrantyFor(vehicle).years,
        warranty_miles: warrantyFor(vehicle).miles,
      };
      const res = await fetch(`${API_BASE}/api/lead/battery-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setResult({
        ...data,
        current_soh: data.current_soh ?? vehicle.sohCurrent,
        epa_range_new: data.epa_range_new ?? vehicle.epaRange,
        delta_30d: data.delta_30d ?? vehicle.delta30d,
        model_year: data.model_year ?? vehicle.year,
        warranty_threshold: data.warranty_threshold ?? (vehicle.warranty?.threshold || 70),
      });
      toast.success("Report generated", {
        description: `PDF being sent to ${email}`,
      });
    } catch (_e) {
      toast.error("Lookup failed", { description: "Something went wrong. Please try again." });
      setError("Lookup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 pb-4" data-testid="mobile-app-view-lookup">
      <SubHeader title={t("app.lookup_title", "Battery score lookup")} back={back} />

      <div>
        <div className="font-display text-base text-white font-semibold leading-snug">
          {t("app.lookup_headline_1", "Any Tesla,")}{" "}
          <span className="brand-text-gradient">
            {t("app.lookup_headline_2", "instant EVIQ Score.")}
          </span>
        </div>
        <div className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
          {t(
            "app.lookup_sub",
            "Drop a VIN prefix — we email a full PDF with degradation, warranty math, and personal recommendations.",
          )}
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2.5">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mb-1">
            Tesla VIN prefix
          </div>
          <input
            type="text"
            value={vin}
            onChange={(e) => setVin(e.target.value)}
            placeholder="5YJ3E1EB7MF"
            data-testid="mobile-app-lookup-vin"
            className="w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono placeholder:text-zinc-600 focus:outline-none focus:border-[#39FF14]/40"
          />
        </div>
        <div>
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mb-1">
            Report email
          </div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            data-testid="mobile-app-lookup-email"
            className="w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#39FF14]/40"
          />
        </div>
        {error && <div className="text-[11px] text-[#FF6B57]">{error}</div>}
        <button
          onClick={submit}
          disabled={loading}
          data-testid="mobile-app-lookup-submit"
          className="w-full brand-gradient text-black font-semibold rounded-lg py-2.5 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} />
              Analyzing…
            </>
          ) : (
            <>
              Get EVIQ Score
              <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
            </>
          )}
        </button>
      </div>

      {result && (
        <>
          <div className="rounded-xl border border-[#39FF14]/25 bg-[#39FF14]/[0.03] p-4">
            <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
              EVIQ Score
            </div>
            <div className="font-display text-4xl text-white font-semibold">
              {result.score ?? result.eviq_score ?? "—"}
              {(result.grade || result.eviq_grade) && (
                <span className="text-base text-[#39FF14] font-mono ml-2">
                  Grade {result.grade || result.eviq_grade}
                </span>
              )}
            </div>
            <div className="text-[11px] text-zinc-400 mt-2 leading-relaxed">
              Full PDF report emailed to <span className="text-white">{email}</span> in a few
              seconds. Includes degradation curve, warranty math, and personalized
              recommendations.
            </div>
          </div>

          <HealthChart
            vehicle={{
              id: `lookup-${vin.toUpperCase()}`,
              short: `VIN ${vin.toUpperCase().slice(0, 8)}`,
              year: result.model_year || 2022,
              sohCurrent: result.current_soh ?? result.soh ?? 92,
              fleetAvg: (result.current_soh ?? result.soh ?? 92) + 1.2,
              delta30d: result.delta_30d ?? -0.25,
              epaRange: result.epa_range_new ?? 340,
              warranty: {
                threshold: result.warranty_threshold ?? 70,
                years: 8,
                miles: 120000,
              },
            }}
            testid="mobile-app-lookup-chart"
          />
        </>
      )}
    </div>
  );
};

// ---- Alerts sub-view ----
const AlertsSubView = ({ back }) => <AlertsOptIn back={back} />;

/** One quick "why" question before the cancel email opens — feedback goes to
 *  /api/cancel-feedback so the operator learns what to fix. */
const CANCEL_REASONS = [
  { id: "too_expensive", label: "Too expensive" },
  { id: "not_using", label: "Not using it enough" },
  { id: "missing_feature", label: "Missing a feature I need" },
  { id: "sold_ev", label: "Sold my EV" },
  { id: "other", label: "Something else" },
];

const CancelPlanButton = ({ email }) => {
  const API = process.env.REACT_APP_BACKEND_URL || "";
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const mailto = `mailto:support@eviq.pro?subject=${encodeURIComponent("Cancel my EVIQ subscription")}&body=${encodeURIComponent(`Please cancel the subscription for ${email || "my account"}.`)}`;

  const proceed = async (withFeedback) => {
    if (withFeedback && reason) {
      setBusy(true);
      try {
        await fetch(`${API}/api/cancel-feedback`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email || null, reason, note: note || null }),
        });
      } catch {}
      setBusy(false);
    }
    setOpen(false);
    window.location.href = mailto;
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex-1 rounded-lg py-2 text-[12px] font-semibold bg-white/10 text-white border border-white/15 text-center"
        data-testid="pricing-cancel-btn"
      >
        Cancel plan
      </button>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 p-4" data-testid="cancel-feedback-dialog">
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0a0a0a] p-5">
            <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5">Before you go</div>
            <h3 className="text-sm text-white font-semibold mb-3">
              One quick question — what's the main reason?
            </h3>
            <div className="space-y-1.5">
              {CANCEL_REASONS.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setReason(r.id)}
                  data-testid={`cancel-reason-${r.id}`}
                  className={`w-full text-left rounded-lg px-3 py-2 text-[12px] border transition-colors ${
                    reason === r.id
                      ? "border-[#39FF14]/60 bg-[#39FF14]/[0.08] text-white"
                      : "border-white/10 bg-white/[0.03] text-zinc-300 hover:bg-white/[0.06]"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            {reason && (
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Anything we could have done better? (optional)"
                rows={2}
                data-testid="cancel-feedback-note"
                className="mt-2.5 w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-[12px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/25 resize-none"
              />
            )}
            <div className="mt-3.5 flex gap-2">
              <button
                onClick={() => proceed(true)}
                disabled={!reason || busy}
                data-testid="cancel-feedback-submit"
                className="flex-1 rounded-lg py-2 text-[12px] font-semibold bg-white text-black disabled:opacity-40"
              >
                {busy ? "…" : "Send & continue to cancel"}
              </button>
              <button
                onClick={() => proceed(false)}
                data-testid="cancel-feedback-skip"
                className="rounded-lg px-3 py-2 text-[12px] text-zinc-400 border border-white/10"
              >
                Skip
              </button>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="cancel-feedback-close"
              className="mt-2 w-full text-center text-[11px] text-zinc-500 hover:text-white transition-colors"
            >
              Never mind — keep my plan
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// ---- Pricing sub-view ----
const PricingSubView = ({ back }) => {
  const { t } = useTranslation();
  const API = process.env.REACT_APP_BACKEND_URL || "";
  const currentTier = getEffectiveTier();
  const paid = hasPaidSubscription();

  const [selected, setSelected] = useState(null); // plan id with the email form open
  const [email, setEmail] = useState(() => {
    try { return window.localStorage.getItem("eviq.subscription_email") || ""; } catch { return ""; }
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [activationId, setActivationId] = useState(null);
  const [unlockedPlan, setUnlockedPlan] = useState(null);
  const [mySub, setMySub] = useState(null);

  // Subscription manager — look up the active plan + renewal date for the
  // stored email so subscribers can see and manage what they're on.
  useEffect(() => {
    const em = (email || "").trim().toLowerCase();
    if (!paid || !em || !/.+@.+\..+/.test(em) || !API) return;
    fetch(`${API}/api/subscribe/me?email=${encodeURIComponent(em)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (j?.active) setMySub(j); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paid, unlockedPlan]);

  // Poll payment status while waiting — the Stripe webhook flips the
  // record to paid the moment the checkout completes. Give up after
  // ~10 minutes so an abandoned checkout doesn't poll forever.
  useEffect(() => {
    if (!activationId || unlockedPlan) return undefined;
    let attempts = 0;
    const id = setInterval(async () => {
      attempts += 1;
      if (attempts > 170) {
        clearInterval(id);
        setActivationId(null);
        setError("We didn't see a payment come through — tap the plan again to retry.");
        return;
      }
      try {
        const r = await fetch(`${API}/api/subscribe/stripe-status/${activationId}`);
        if (!r.ok) return;
        const j = await r.json();
        if (j.paid) {
          markSubscribed("active", j.plan_id || selected);
          setUnlockedPlan(j.plan_id || selected);
          toast.success("Payment confirmed — your plan is active!");
        }
      } catch { /* transient poll error — keep trying */ }
    }, 3500);
    return () => clearInterval(id);
  }, [activationId, unlockedPlan, selected, API]);

  const startCheckout = async (planId) => {
    setError("");
    if (!email || !/.+@.+\..+/.test(email)) {
      setError("Enter a valid email so we can activate your plan automatically.");
      return;
    }
    setSubmitting(true);
    // Open the tab synchronously (inside the tap) so popup blockers
    // don't eat the Stripe redirect after the async fetch resolves.
    const w = window.open("about:blank", "_blank");
    let refCode = null;
    try { refCode = window.localStorage.getItem("eviq.referral_code") || null; } catch { /* noop */ }
    try {
      const res = await fetch(`${API}/api/subscribe/stripe-start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), plan_id: planId, referral_code: refCode }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Could not start checkout — please try again.");
      }
      const data = await res.json();
      try { window.localStorage.setItem("eviq.subscription_email", email.trim().toLowerCase()); } catch { /* noop */ }
      if (w) w.location = data.checkout_url;
      else window.open(data.checkout_url, "_blank", "noopener,noreferrer");
      setActivationId(data.activation_id);
    } catch (e2) {
      if (w) w.close();
      setError(e2.message || "Could not start checkout — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const plans = [
    {
      id: "basic",
      name: "Basic",
      price: "$9.99",
      period: "/month",
      cta: "Subscribe to Basic",
      features: [
        "Live SoH monitoring",
        "Battery Intelligence Passport + share link",
        "Recall check + new-recall alerts",
        "Warranty alerts",
        "Monitors 1 vehicle",
      ],
    },
    {
      id: "yearly",
      name: "Premium",
      price: "$99.99",
      period: "/year · save 17%",
      highlight: true,
      cta: "Go Premium",
      features: [
        "Everything in Basic",
        "AI Coach + Battery Life Predictor",
        "Warranty claim PDF",
        "Recall Compliance Report (seller PDF)",
        "Up to 3 vehicles",
      ],
    },
    {
      id: "lifetime",
      name: "Lifetime",
      price: "$199.99",
      period: "one-time",
      cta: "Get Lifetime",
      features: [
        "Everything in Premium — all categories",
        "Cell health map + resale value",
        "Every future feature included",
        "No renewals · transfers to your next EV",
        "Up to 5 vehicles",
      ],
    },
  ];

  return (
    <div className="space-y-3 pb-4" data-testid="mobile-app-view-pricing">
      <SubHeader title={t("app.more_pricing", "Plans & pricing")} back={back} />

      {paid && !unlockedPlan && (
        <div className="rounded-xl border border-[#39FF14]/30 bg-[#39FF14]/[0.05] p-4" data-testid="pricing-current-plan">
          <div className="flex items-baseline justify-between">
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">Your subscription</div>
            <span className="font-mono text-[9px] uppercase text-[#39FF14] bg-[#39FF14]/10 border border-[#39FF14]/30 rounded px-1.5 py-0.5">Active</span>
          </div>
          <div className="text-sm text-white font-semibold mt-1" data-testid="pricing-my-plan">
            {mySub?.plan_label || `${currentTier.charAt(0).toUpperCase()}${currentTier.slice(1)} plan`}
          </div>
          <p className="font-mono text-[10px] text-zinc-400 mt-1" data-testid="pricing-renewal">
            {mySub?.plan_id === "lifetime"
              ? "Lifetime access — never renews."
              : mySub?.renews_at
                ? `Renews ${new Date(mySub.renews_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}.`
                : "Choosing another plan below switches you at checkout."}
          </p>
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => { setSelected("yearly"); setError(""); }}
              className="flex-1 rounded-lg py-2 text-[12px] font-semibold bg-[#39FF14] text-black"
              data-testid="pricing-upgrade-btn"
            >
              Upgrade plan
            </button>
            <CancelPlanButton email={email} />
          </div>
          <p className="font-mono text-[9px] text-zinc-600 mt-2">
            Cancellations take effect at the end of the paid period — no partial-period charges.
          </p>
        </div>
      )}

      {paid && <ReferralCard />}

      {unlockedPlan && (
        <div className="rounded-xl border border-[#39FF14]/40 bg-[#39FF14]/[0.08] p-4" data-testid="pricing-unlocked">
          <div className="text-sm text-white font-semibold">You're all set 🎉</div>
          <p className="font-mono text-[10px] text-zinc-400 mt-1">
            Your {unlockedPlan === "yearly" ? "Premium" : unlockedPlan} plan is active on this device.
          </p>
        </div>
      )}

      {activationId && !unlockedPlan && (
        <div className="rounded-xl border border-[#00E5FF]/30 bg-[#00E5FF]/[0.05] p-3 flex items-center gap-2" data-testid="pricing-waiting">
          <span className="w-2 h-2 rounded-full bg-[#00E5FF] animate-pulse shrink-0" />
          <p className="font-mono text-[10px] text-zinc-300 leading-relaxed">
            Waiting for Stripe to confirm your payment… this unlocks automatically — keep this screen open.
          </p>
        </div>
      )}

      {plans.map((p) => (
        <div
          key={p.id}
          className={`rounded-xl border p-4 ${
            p.highlight ? "border-[#39FF14]/40 bg-[#39FF14]/[0.04]" : "border-white/10 bg-white/[0.02]"
          }`}
          data-testid={`pricing-plan-${p.id}`}
        >
          <div className="flex items-baseline justify-between">
            <div className="text-sm text-white font-semibold">{p.name}</div>
            {p.highlight && (
              <span className="font-mono text-[9px] uppercase text-[#39FF14] bg-[#39FF14]/10 border border-[#39FF14]/30 rounded px-1.5 py-0.5">
                Most popular
              </span>
            )}
          </div>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className="font-display text-2xl text-white font-semibold">{p.price}</span>
            <span className="text-[11px] text-zinc-500 font-mono">{p.period}</span>
          </div>
          <ul className="mt-3 space-y-1.5">
            {p.features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-[11px] text-zinc-300 leading-snug">
                <Check className="w-3 h-3 mt-0.5 shrink-0" style={{ color: "#39FF14" }} strokeWidth={3} />
                {f}
              </li>
            ))}
          </ul>

          {selected === p.id ? (
            <div className="mt-3 space-y-2">
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-[13px] text-white font-mono focus:outline-none focus:border-[#39FF14]/50"
                data-testid={`pricing-email-input-${p.id}`}
              />
              {error && (
                <p className="font-mono text-[10px] text-[#FF6B57]" data-testid="pricing-error">{error}</p>
              )}
              <button
                onClick={() => startCheckout(p.id)}
                disabled={submitting}
                className={`w-full rounded-lg py-2.5 text-[13px] font-semibold transition-opacity disabled:opacity-40 ${
                  p.highlight ? "bg-[#39FF14] text-black" : "bg-white/10 text-white border border-white/15"
                }`}
                data-testid={`pricing-checkout-${p.id}`}
              >
                {submitting ? "Opening secure checkout…" : `Pay ${p.price} with Stripe`}
              </button>
              <p className="font-mono text-[9px] text-zinc-600 text-center">
                Stripe secure checkout · activates automatically · cancel anytime by email
              </p>
            </div>
          ) : (
            <button
              onClick={() => { setSelected(p.id); setError(""); }}
              className={`mt-3 w-full rounded-lg py-2.5 text-[13px] font-semibold ${
                p.highlight ? "bg-[#39FF14] text-black" : "bg-white/10 text-white border border-white/15"
              }`}
              data-testid={`pricing-subscribe-${p.id}`}
            >
              {p.cta}
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

// ---- Cells sub-view ----
const CellsSubView = ({ back }) => {
  const { t } = useTranslation();
  const { vehicle, live } = useVehicle();
  const [selected, setSelected] = useState(null);

  // "Connected" here means an authenticated Tesla OAuth session exists —
  // even if the Fleet API hasn't returned vehicle data yet. This hides the
  // "Connect your Tesla" CTA the moment sign-in completes, so returning
  // users never see a redundant connect button.
  const connected = !!live?.connected;
  const vin = vehicle?.vin || null;

  const cells = useMemo(() => buildCells(vehicle, 120), [vehicle]);
  const counts = useMemo(() => {
    const c = { healthy: 0, watch: 0, critical: 0 };
    cells.forEach((cell) => (c[cell.bucket] += 1));
    return c;
  }, [cells]);
  const total = cells.length;

  return (
    <div className="space-y-4 pb-4" data-testid="mobile-app-view-cells">
      <SubHeader
        title={t("app.cells_title", "Cell health map")}
        back={back}
      />

      <div
        className="rounded-xl border p-3 flex items-center gap-3"
        style={{
          borderColor: connected ? "rgba(57,255,20,0.35)" : "rgba(255,176,32,0.35)",
          background: connected ? "rgba(57,255,20,0.05)" : "rgba(255,176,32,0.05)",
        }}
        data-testid="cells-connection-banner"
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border"
          style={{
            background: connected ? "rgba(57,255,20,0.1)" : "rgba(255,176,32,0.1)",
            borderColor: connected ? "rgba(57,255,20,0.35)" : "rgba(255,176,32,0.35)",
          }}
        >
          {connected ? (
            <WifiIcon className="w-4 h-4" style={{ color: "#39FF14" }} strokeWidth={2.5} />
          ) : (
            <WifiOff className="w-4 h-4" style={{ color: "#FFB020" }} strokeWidth={2.5} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-white font-semibold leading-tight">
            {connected ? "Tesla Fleet API · connected" : "Tesla not connected"}
          </div>
          <div className="font-mono text-[10px] text-zinc-400 leading-relaxed">
            {connected
              ? `VIN ${vin} · cells modeled from pack-level SoH (Fleet API doesn't expose per-cell voltages)`
              : "Showing synthesized cells based on VIN + last-known SoH"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2" data-testid="cells-summary">
        {Object.entries(BUCKETS).map(([key, b]) => {
          const n = counts[key];
          const pct = total ? Math.round((n / total) * 100) : 0;
          return (
            <div
              key={key}
              className="rounded-xl border p-2.5"
              style={{
                borderColor: `${b.color}44`,
                background: `${b.color}0f`,
              }}
              data-testid={`cells-summary-${key}`}
            >
              <div
                className="font-mono text-[9px] uppercase tracking-wider"
                style={{ color: b.color }}
              >
                {b.label}
              </div>
              <div className="font-display text-xl text-white font-semibold mt-1">
                {n}
                <span className="text-[10px] text-zinc-500 font-mono ml-1">{pct}%</span>
              </div>
              <div className="font-mono text-[9px] text-zinc-500 mt-0.5">{b.desc}</div>
            </div>
          );
        })}
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-2 px-1">
          <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
            Pack view · 120 cells
          </span>
          <span className="font-mono text-[10px] text-zinc-500">
            Mean {vehicle.sohCurrent}%
          </span>
        </div>
        <div
          className="grid gap-[3px] p-2 rounded-xl border border-white/10 bg-white/[0.015]"
          style={{ gridTemplateColumns: "repeat(10, 1fr)" }}
          data-testid="cells-grid"
        >
          {cells.map((cell) => {
            const active = selected?.i === cell.i;
            const color = BUCKETS[cell.bucket].color;
            return (
              <button
                key={cell.i}
                onClick={() => setSelected(cell)}
                aria-label={`Cell ${cell.i + 1}: ${cell.health}%`}
                data-testid={`cell-${cell.i}`}
                className="aspect-square rounded-sm transition-transform hover:scale-110 focus:outline-none"
                style={{
                  background: color,
                  opacity: active ? 1 : 0.85,
                  boxShadow: active ? `0 0 8px ${color}` : "none",
                  transform: active ? "scale(1.15)" : "scale(1)",
                }}
              />
            );
          })}
        </div>
      </div>

      {selected && (
        <div
          className="rounded-xl border border-white/10 bg-white/[0.02] p-3"
          data-testid="cells-detail"
        >
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-sm text-white font-semibold">
              Cell #{selected.i + 1}
            </span>
            <span
              className="font-mono text-[10px] px-1.5 py-0.5 rounded border"
              style={{
                color: BUCKETS[selected.bucket].color,
                borderColor: `${BUCKETS[selected.bucket].color}55`,
                background: `${BUCKETS[selected.bucket].color}12`,
              }}
            >
              {BUCKETS[selected.bucket].label}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div>
              <div className="font-mono text-[9px] uppercase text-zinc-500">Health</div>
              <div className="font-mono text-sm text-white mt-0.5">{selected.health}%</div>
            </div>
            <div>
              <div className="font-mono text-[9px] uppercase text-zinc-500">Voltage</div>
              <div className="font-mono text-sm text-white mt-0.5">{selected.voltage} V</div>
            </div>
          </div>
        </div>
      )}

      <div className="pt-2 border-t border-white/5 flex items-center gap-3 flex-wrap">
        {Object.entries(BUCKETS).map(([key, b]) => (
          <div key={key} className="flex items-center gap-1.5">
            <span
              className="w-2.5 h-2.5 rounded-sm"
              style={{ background: b.color }}
            />
            <span className="font-mono text-[10px] text-zinc-400">
              {b.label} · {b.desc}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
